import type { TocEntry } from "@msb/reader-bridge";
import { useEffect, useRef } from "preact/hooks";
import { defineMessages, useT } from "../i18n/index.ts";

interface Props {
  toc: TocEntry[];
  currentHref?: string;
  /** Extrait : les parties absentes du fichier restent visibles, grisées et sans lien. */
  isAvailable?(href: string): boolean;
  onSelect(href: string): void;
}

const messages = defineMessages(
  { untitled: "Sans titre", empty: "Ce livre n'a pas de table des matières.", unavailable: "Pas dans l'extrait" },
  { untitled: "Untitled", empty: "This book has no table of contents.", unavailable: "Not in the excerpt" },
);

const base = (href: string) => href.split("#")[0];

/**
 * L'entrée de la page lue, comme Studio : la première de son fichier dans l'ordre de la table. Un chapitre et ses
 * parties (I, II, III… dans le même fichier) ne sont pas tous surlignés : seul le titre du chapitre l'est.
 */
export function currentEntry(toc: TocEntry[], currentHref: string | undefined): TocEntry | undefined {
  if (currentHref === undefined) return undefined;
  for (const link of toc) {
    if (base(link.href) === base(currentHref)) return link;
    const inside = currentEntry(link.children, currentHref);
    if (inside) return inside;
  }
  return undefined;
}

interface EntriesProps {
  links: TocEntry[];
  current?: TocEntry;
  isAvailable?(href: string): boolean;
  onSelect(href: string): void;
  depth: number;
}

function Entries({ links, current, isAvailable, onSelect, depth }: EntriesProps) {
  const t = useT(messages);
  return (
    <ol class={depth === 0 ? "msb-list" : "msb-list msb-list--nested"}>
      {links.map((link, i) => {
        const available = !isAvailable || isAvailable(link.href);
        return (
          <li key={`${link.href}-${i}`}>
            <button
              type="button"
              class={available ? "msb-list__item" : "msb-list__item msb-list__item--unavailable"}
              aria-current={link === current ? "location" : undefined}
              aria-disabled={available ? undefined : "true"}
              title={available ? undefined : t("unavailable")}
              onClick={available ? () => onSelect(link.href) : undefined}
            >
              {link.title || t("untitled")}
            </button>
            {link.children.length > 0 && (
              <Entries links={link.children} current={current} isAvailable={isAvailable} onSelect={onSelect} depth={depth + 1} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** Une entrée est dans l'extrait si son fichier fait partie de l'ordre de lecture. */
export function availableIn(readingOrder: string[]): (href: string) => boolean {
  const files = new Set(readingOrder.map(base));
  return (href) => files.has(base(href));
}

export function ContentsPanel({ toc, currentHref, isAvailable, onSelect }: Props) {
  const t = useT(messages);
  const list = useRef<HTMLDivElement>(null);
  const current = currentEntry(toc, currentHref);
  // À l'ouverture, le chapitre en cours est montré même au fond d'une longue table.
  useEffect(() => {
    list.current?.querySelector('[aria-current="location"]')?.scrollIntoView?.({ block: "center" });
  }, []);
  if (toc.length === 0) return <p class="msb-empty">{t("empty")}</p>;
  return (
    <div ref={list}>
      <Entries links={toc} current={current} isAvailable={isAvailable} onSelect={onSelect} depth={0} />
    </div>
  );
}
